import type {
  BulkCreateProcessorCostSchedulesRequest,
  CreateProcessorCostScheduleRequest,
  ProcessorCostScheduleResponse,
  ReplaceProcessorCostScheduleRequest,
  UpdateProcessorCostScheduleRequest,
} from '../types';
import type { RequestFn } from '../client';

/**
 * A merchant's own processor cost schedules: what the processor the merchant
 * brought itself charges it, per connector and — as narrowly as the rate is
 * written — per shop, payment method, method type, card network or the
 * processor's own method code.
 *
 * A schedule prices the rails that report no fee on the payment itself. What
 * it produces is stamped `estimated` on the settlement line, never
 * `reported`, and the rates that priced it are copied onto the line as
 * `processor_cost_basis` so the figure survives the schedule being replaced
 * or deleted.
 *
 * The DeloPay-wide defaults a merchant inherits are DeloPay's own acquirer
 * pricing. They are never listed here, and they are edited through the
 * internal client instead (`DelopayInternal.adminPortal`).
 *
 * A rate is never edited in place: it priced transactions that already
 * happened. {@link ProcessorCosts.update} only closes, deactivates or
 * annotates a schedule; a price change is {@link ProcessorCosts.replace},
 * which closes the old window and opens the successor in one step.
 *
 * Every route takes the merchant account id and needs a dashboard session for
 * that merchant.
 */
export class ProcessorCosts {
  constructor(private readonly request: RequestFn) {}

  /**
   * Every processor cost schedule of the merchant.
   * `GET /processor-costs`
   *
   * @param merchantId - The merchant account ID.
   */
  async list(merchantId: string): Promise<ProcessorCostScheduleResponse[]> {
    return this.request('GET', '/processor-costs', { query: { merchant_id: merchantId } });
  }

  /**
   * Record a processor cost schedule.
   * `POST /processor-costs`
   *
   * A `shop_id` that is well formed but not a shop of this merchant is
   * answered `404` and nothing is recorded: a rate scoped to a shop the
   * merchant does not own would price no attempt, leaving every line it was
   * meant to price `unavailable` while the screen showed a configured rate.
   *
   * @param params - The schedule.
   * @param merchantId - The merchant account ID.
   */
  async create(
    params: CreateProcessorCostScheduleRequest,
    merchantId: string,
  ): Promise<ProcessorCostScheduleResponse> {
    return this.request('POST', '/processor-costs', {
      body: params,
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Record several schedules at once, all or none — copy a price list across
   * methods or shops.
   * `POST /processor-costs/bulk`
   *
   * 1 to 200 entries. Every entry is validated before anything is stored: an
   * invalid one is answered `422` naming its index, and nothing is stored.
   * The answer lists the created schedules in request order.
   *
   * @param params - The schedules.
   * @param merchantId - The merchant account ID.
   */
  async bulkCreate(
    params: BulkCreateProcessorCostSchedulesRequest,
    merchantId: string,
  ): Promise<ProcessorCostScheduleResponse[]> {
    return this.request('POST', '/processor-costs/bulk', {
      body: params,
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Close, deactivate or annotate a schedule. The rate is not editable.
   * `PUT /processor-costs/{schedule_id}`
   *
   * @param scheduleId - The schedule ID.
   * @param params - What to change.
   * @param merchantId - The merchant account ID.
   */
  async update(
    scheduleId: string,
    params: UpdateProcessorCostScheduleRequest,
    merchantId: string,
  ): Promise<ProcessorCostScheduleResponse> {
    return this.request('PUT', `/processor-costs/${encodeURIComponent(scheduleId)}`, {
      body: params,
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Close a schedule at `effective_from` and open its successor with the same
   * scope and the same end, in one step: no transaction is priced by both
   * rates, and none falls between them.
   * `POST /processor-costs/{schedule_id}/replace`
   *
   * Answers the successor schedule. An `effective_from` outside the old
   * schedule's window, an inactive schedule, or one a concurrent replacement
   * already moved is answered `400` — retry against the schedule as it now
   * stands rather than forcing the write.
   *
   * @param scheduleId - The schedule to replace.
   * @param params - The new rate.
   * @param merchantId - The merchant account ID.
   */
  async replace(
    scheduleId: string,
    params: ReplaceProcessorCostScheduleRequest,
    merchantId: string,
  ): Promise<ProcessorCostScheduleResponse> {
    return this.request('POST', `/processor-costs/${encodeURIComponent(scheduleId)}/replace`, {
      body: params,
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Delete a schedule. Restates nothing: every figure it derived is already
   * stamped on the settlement lines it priced.
   * `DELETE /processor-costs/{schedule_id}`
   *
   * @param scheduleId - The schedule ID.
   * @param merchantId - The merchant account ID.
   */
  async delete(scheduleId: string, merchantId: string): Promise<void> {
    return this.request('DELETE', `/processor-costs/${encodeURIComponent(scheduleId)}`, {
      query: { merchant_id: merchantId },
    });
  }
}
